import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit, Trash, Wallet, History, Search, Eye, EyeOff, TrendingUp, TrendingDown, MessageSquare } from 'lucide-react';
import BottomNavigation from '../components/BottomNavigation';
import LogoLoader from '../components/LogoLoader';
import FOSWatermark from '../components/FOSWatermark';
import GuidedTour from '../components/GuidedTour';
import { TOUR_DEFINITIONS } from '../services/tourService';
import { LedgerPartySkeleton, TransactionsSkeleton } from '../components/SkeletonLoaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LedgerManagement = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isAddPartyOpen, setIsAddPartyOpen] = useState(false);
  const [isEditPartyOpen, setIsEditPartyOpen] = useState(false);
  const [isAddMoneyOpen, setIsAddMoneyOpen] = useState(false);
  const [isTransactionsOpen, setIsTransactionsOpen] = useState(false);
  const [selectedParty, setSelectedParty] = useState(null);
  const [newParty, setNewParty] = useState({ party_name: '', phone_number: '' });
  const [editParty, setEditParty] = useState({ party_name: '', phone_number: '' });
  const [addMoneyData, setAddMoneyData] = useState({ amount: '', description: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [isSummaryVisible, setIsSummaryVisible] = useState(false);
  const [isVerifySummaryPinOpen, setIsVerifySummaryPinOpen] = useState(false);
  const [summaryPin, setSummaryPin] = useState('');
  const [summaryTimer, setSummaryTimer] = useState(null);
  
  // Tour state
  const [forceTour, setForceTour] = useState(searchParams.get('tour') === 'true');

  useEffect(() => {
    const loadData = async () => {
      setInitialLoading(true);
      await fetchParties();
      setInitialLoading(false);
    };
    loadData();
  }, []);

  // Cleanup summary timer on unmount
  useEffect(() => {
    return () => {
      if (summaryTimer) {
        clearTimeout(summaryTimer);
      }
    };
  }, [summaryTimer]);

  const fetchParties = async () => {
    try {
      const response = await axios.get(`${API}/ledger/parties?franchise_id=${user.franchise.id}`);
      const serverParties = response.data.parties || [];
      setParties(serverParties);
    } catch (error) {
      console.error('Error fetching parties:', error);
      toast.error('Failed to load parties');
    }
  };

  const handleAddParty = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/ledger/parties`, {
        party_name: newParty.party_name,
        phone_number: newParty.phone_number,
        franchise_id: user.franchise.id
      });
      toast.success('Party added successfully!');
      setNewParty({ party_name: '', phone_number: '' });
      setIsAddPartyOpen(false);
      fetchParties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add party');
    } finally {
      setLoading(false);
    }
  };

  const handleEditParty = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/ledger/parties/${selectedParty.party_id}`, {
        party_name: editParty.party_name,
        phone_number: editParty.phone_number
      });
      toast.success('Party updated successfully!');
      setIsEditPartyOpen(false);
      setSelectedParty(null);
      fetchParties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update party');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleWhatsApp = async (partyId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await axios.patch(`${API}/ledger/parties/${partyId}/whatsapp?enabled=${newStatus}`);
      
      // Update local state
      setParties(parties.map(p => 
        p.party_id === partyId ? { ...p, whatsapp_enabled: newStatus } : p
      ));
      
      toast.success(`WhatsApp notifications ${newStatus ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update WhatsApp setting');
    }
  };

  const handleDeleteParty = async (partyId) => {
    if (!window.confirm('Are you sure you want to delete this party?')) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API}/ledger/parties/${partyId}`);
      toast.success('Party deleted successfully!');
      fetchParties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete party');
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary
  const calculateSummary = () => {
    const moneyAvailable = parties
      .filter(p => p.balance > 0)
      .reduce((sum, p) => sum + parseFloat(p.balance), 0);
    
    const moneyToReceive = Math.abs(
      parties
        .filter(p => p.balance < 0)
        .reduce((sum, p) => sum + parseFloat(p.balance), 0)
    );

    return { moneyAvailable, moneyToReceive };
  };

  const handleViewSummaryClick = () => {
    setIsVerifySummaryPinOpen(true);
  };

  const handleVerifySummaryPin = async (e) => {
    e.preventDefault();

    if (!summaryPin) {
      toast.error('Please enter your PIN');
      return;
    }

    try {
      const response = await axios.post(`${API}/franchises/verify-pin`, {
        franchise_id: user.franchise.id,
        pin: summaryPin
      });

      if (response.data.success) {
        setIsSummaryVisible(true);
        setIsVerifySummaryPinOpen(false);
        setSummaryPin('');
        toast.success('Summary unlocked for 10 seconds');

        // Clear any existing timer
        if (summaryTimer) {
          clearTimeout(summaryTimer);
        }

        // Auto-hide after 10 seconds
        const timer = setTimeout(() => {
          setIsSummaryVisible(false);
          toast.info('Summary hidden');
        }, 10000);

        setSummaryTimer(timer);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid PIN');
      setSummaryPin('');
    }
  };

  const handleHideSummary = () => {
    if (summaryTimer) {
      clearTimeout(summaryTimer);
    }
    setIsSummaryVisible(false);
    toast.info('Summary hidden');
  };

  const handleAddMoney = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const amount = parseFloat(addMoneyData.amount);
      const description = addMoneyData.description || 'Money added to ledger';
      
      await axios.post(`${API}/ledger/parties/${selectedParty.party_id}/add-money`, {
        amount: amount,
        description: description
      });
      toast.success('Money added successfully!');
      
      setAddMoneyData({ amount: '', description: '' });
      setIsAddMoneyOpen(false);
      setSelectedParty(null);
      fetchParties();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to add money');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (party) => {
    setSelectedParty(party);
    setEditParty({
      party_name: party.party_name,
      phone_number: party.phone_number
    });
    setIsEditPartyOpen(true);
  };

  const openAddMoneyDialog = (party) => {
    setSelectedParty(party);
    setAddMoneyData({ amount: '', description: '' });
    setIsAddMoneyOpen(true);
  };

  const openTransactionsDialog = async (party) => {
    setSelectedParty(party);
    setIsTransactionsOpen(true);
    
    try {
      const response = await axios.get(`${API}/ledger/transactions?party_id=${party.party_id}`);
      const transactions = response.data.transactions || [];
      setSelectedParty({ ...party, transactions });
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Failed to load transaction history');
    }
  };

  // Filter parties based on search
  const filteredParties = parties.filter(party =>
    party.party_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    party.phone_number.includes(searchQuery)
  );

  const summary = calculateSummary();

  return (
    <div className="min-h-screen bg-background">
      {/* Ledger Management Tour */}
      <GuidedTour
        tourId="ledgerManagement"
        steps={TOUR_DEFINITIONS.ledgerManagement?.steps || []}
        forceRun={forceTour}
        onComplete={() => {
          setForceTour(false);
          toast.success('Ledger Management tour completed!');
        }}
      />
      
      {/* Initial Loading Screen */}
      {initialLoading && (
        <div className="fixed inset-0 bg-background z-50 flex items-center justify-center">
          <LogoLoader size="lg" text="Loading Ledger..." />
        </div>
      )}
      
      {/* Action Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <LogoLoader size="md" text="Processing..." />
        </div>
      )}
      
      {/* Top Header Bar */}
      <div className="sticky top-0 z-40 bg-surface border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              data-testid="back-button"
              variant="ghost"
              size="sm"
              className="h-10 w-10 p-0 rounded-full"
              onClick={() => navigate('/franchise')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-primary">Ledger Management</h1>
              <p className="text-xs text-secondary">{user.franchise.name}</p>
            </div>
          </div>
          <Dialog open={isAddPartyOpen} onOpenChange={setIsAddPartyOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-party-trigger"
                className="h-10 bg-primary text-white hover:bg-primary/90 rounded-full px-4 font-bold"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Party
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-surface">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">Add New Party</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddParty} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="party-name">Party Name</Label>
                  <Input
                    id="party-name"
                    data-testid="party-name-input"
                    value={newParty.party_name}
                    onChange={(e) => setNewParty({ ...newParty, party_name: e.target.value })}
                    className="h-12 rounded-xl"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="party-phone">Phone Number</Label>
                  <Input
                    id="party-phone"
                    data-testid="party-phone-input"
                    type="tel"
                    value={newParty.phone_number}
                    onChange={(e) => setNewParty({ ...newParty, phone_number: e.target.value })}
                    className="h-12 rounded-xl"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="submit-party-button"
                  disabled={loading}
                  className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full font-bold uppercase tracking-wider"
                >
                  {loading ? 'Adding...' : 'Add Party'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        {/* Summary Cards - PIN Protected */}
        {parties.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-primary">Financial Summary</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={isSummaryVisible ? handleHideSummary : handleViewSummaryClick}
                className="h-8 px-3 rounded-full hover:bg-accent/10"
                title={isSummaryVisible ? "Hide summary (auto-hides in 10s)" : "View summary (requires PIN)"}
              >
                {isSummaryVisible ? <EyeOff className="h-4 w-4 text-accent mr-2" /> : <Eye className="h-4 w-4 text-accent mr-2" />}
                <span className="text-sm">{isSummaryVisible ? 'Hide' : 'View'}</span>
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Money Available Card */}
              <Card className="shadow-sm border-border bg-gradient-to-br from-green-50 to-emerald-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-green-500 rounded-full">
                      <TrendingUp className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="font-semibold text-green-900">Money Available</h3>
                  </div>
                  {isSummaryVisible ? (
                    <div>
                      <p className="text-3xl font-bold text-green-600">₹{summary.moneyAvailable.toFixed(2)}</p>
                      <p className="text-xs text-green-700 mt-1 flex items-center gap-1">
                        <span className="w-2 h-2 bg-orange-600 rounded-full animate-pulse"></span>
                        Auto-hides in 10s
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-3xl font-bold text-secondary">₹ •••••</p>
                      <p className="text-xs text-secondary mt-1">Click eye to view</p>
                    </div>
                  )}
                  <p className="text-xs text-green-700 mt-2">Total positive balances</p>
                </CardContent>
              </Card>

              {/* Money to Receive Card */}
              <Card className="shadow-sm border-border bg-gradient-to-br from-red-50 to-rose-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-red-500 rounded-full">
                      <TrendingDown className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="font-semibold text-red-900">Money to Receive</h3>
                  </div>
                  {isSummaryVisible ? (
                    <div>
                      <p className="text-3xl font-bold text-red-600">₹{summary.moneyToReceive.toFixed(2)}</p>
                      <p className="text-xs text-red-700 mt-1 flex items-center gap-1">
                        <span className="w-2 h-2 bg-orange-600 rounded-full animate-pulse"></span>
                        Auto-hides in 10s
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-3xl font-bold text-secondary">₹ •••••</p>
                      <p className="text-xs text-secondary mt-1">Click eye to view</p>
                    </div>
                  )}
                  <p className="text-xs text-red-700 mt-2">Total negative balances</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Search Bar */}
        {parties.length > 0 && (
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary" />
              <Input
                type="text"
                placeholder="Search by party name or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-12 pl-10 pr-4 rounded-full border-2 focus:border-accent"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary hover:text-primary"
                >
                  ✕
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="text-sm text-secondary mt-2">
                Found {filteredParties.length} of {parties.length} parties
              </p>
            )}
          </div>
        )}

        {initialLoading ? (
          <LedgerPartySkeleton count={6} />
        ) : parties.length === 0 ? (
          <Card className="shadow-sm border-border">
            <CardContent className="p-12 text-center">
              <Wallet className="h-16 w-16 mx-auto text-muted mb-4" />
              <p className="text-lg text-secondary mb-4">No parties added yet</p>
              <p className="text-sm text-secondary mb-6">Add a party to start managing ledgers</p>
              <Button
                onClick={() => setIsAddPartyOpen(true)}
                className="h-12 bg-primary text-white hover:bg-primary/90 rounded-full px-6 font-bold"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Party
              </Button>
            </CardContent>
          </Card>
        ) : filteredParties.length === 0 ? (
          <Card className="shadow-sm border-border">
            <CardContent className="p-12 text-center">
              <Search className="h-16 w-16 mx-auto text-muted mb-4" />
              <p className="text-lg text-secondary mb-2">No parties found</p>
              <p className="text-sm text-secondary mb-6">Try a different search term</p>
              <Button
                onClick={() => setSearchQuery('')}
                variant="outline"
                className="h-10 rounded-full px-6"
              >
                Clear Search
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="party-list grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredParties.map((party) => (
              <Card
                key={party.party_id}
                data-testid={`party-card-${party.party_id}`}
                className="shadow-sm border-border hover:shadow-md transition-shadow"
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl font-bold text-primary">{party.party_name}</CardTitle>
                      <p className="text-sm text-secondary">{party.phone_number}</p>
                    </div>
                    {/* WhatsApp Toggle */}
                    <button
                      data-testid={`whatsapp-toggle-${party.party_id}`}
                      onClick={() => handleToggleWhatsApp(party.party_id, party.whatsapp_enabled !== false)}
                      className={`p-2 rounded-full transition-colors ${
                        party.whatsapp_enabled !== false
                          ? 'bg-green-100 text-green-600 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                      }`}
                      title={party.whatsapp_enabled !== false ? 'WhatsApp notifications ON - Click to disable' : 'WhatsApp notifications OFF - Click to enable'}
                    >
                      <MessageSquare className="h-5 w-5" />
                    </button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-muted rounded-xl">
                    <p className="text-sm text-secondary mb-1">Current Balance</p>
                    <p
                      className={`text-3xl font-bold ${
                        party.balance >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      ₹{parseFloat(party.balance).toFixed(2)}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      data-testid={`add-money-${party.party_id}`}
                      variant="outline"
                      size="sm"
                      className="w-full rounded-full"
                      onClick={() => openAddMoneyDialog(party)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Money
                    </Button>
                    <Button
                      data-testid={`view-history-${party.party_id}`}
                      variant="outline"
                      size="sm"
                      className="w-full rounded-full"
                      onClick={() => openTransactionsDialog(party)}
                    >
                      <History className="h-4 w-4 mr-1" />
                      History
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      data-testid={`edit-party-${party.party_id}`}
                      variant="ghost"
                      size="sm"
                      className="w-full rounded-full"
                      onClick={() => openEditDialog(party)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      data-testid={`delete-party-${party.party_id}`}
                      variant="ghost"
                      size="sm"
                      className="w-full rounded-full text-red-600 hover:text-red-700"
                      onClick={() => handleDeleteParty(party.party_id, party.party_name)}
                    >
                      <Trash className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit Party Dialog */}
      <Dialog open={isEditPartyOpen} onOpenChange={setIsEditPartyOpen}>
        <DialogContent className="bg-surface">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Edit Party</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditParty} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="edit-party-name">Party Name</Label>
              <Input
                id="edit-party-name"
                value={editParty.party_name}
                onChange={(e) => setEditParty({ ...editParty, party_name: e.target.value })}
                className="h-12 rounded-xl"
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-party-phone">Phone Number</Label>
              <Input
                id="edit-party-phone"
                type="tel"
                value={editParty.phone_number}
                onChange={(e) => setEditParty({ ...editParty, phone_number: e.target.value })}
                className="h-12 rounded-xl"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full font-bold uppercase tracking-wider"
            >
              {loading ? 'Updating...' : 'Update Party'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Money Dialog */}
      <Dialog open={isAddMoneyOpen} onOpenChange={setIsAddMoneyOpen}>
        <DialogContent className="bg-surface">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              Add Money - {selectedParty?.party_name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddMoney} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="add-money-amount">Amount (₹)</Label>
              <Input
                id="add-money-amount"
                data-testid="add-money-amount-input"
                type="number"
                step="0.01"
                value={addMoneyData.amount}
                onChange={(e) => setAddMoneyData({ ...addMoneyData, amount: e.target.value })}
                className="h-12 rounded-xl"
                required
              />
            </div>
            <div>
              <Label htmlFor="add-money-description">Description (Optional)</Label>
              <Input
                id="add-money-description"
                data-testid="add-money-description-input"
                value={addMoneyData.description}
                onChange={(e) => setAddMoneyData({ ...addMoneyData, description: e.target.value })}
                className="h-12 rounded-xl"
                placeholder="e.g., Cash payment received"
              />
            </div>
            <Button
              type="submit"
              data-testid="submit-add-money-button"
              disabled={loading}
              className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full font-bold uppercase tracking-wider"
            >
              {loading ? 'Adding...' : 'Add Money'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transaction History Dialog */}
      <Dialog open={isTransactionsOpen} onOpenChange={setIsTransactionsOpen}>
        <DialogContent className="bg-surface max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              Transaction History - {selectedParty?.party_name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {selectedParty?.transactions && selectedParty.transactions.length > 0 ? (
              <div className="space-y-3">
                {selectedParty.transactions.map((txn, index) => {
                  // Check if transaction is an order (description format: "Order #{order_no}")
                  const isOrder = txn.description && (txn.description.startsWith('Order #') || txn.description.startsWith('Order '));
                  const orderNo = isOrder ? txn.description.replace('Order #', '').replace('Order ', '') : (txn.order_no || null);
                  
                  // Get date from created_at or date_time (support both old and new format)
                  const txnDate = txn.created_at || txn.date_time;
                  const formattedDate = txnDate ? new Date(txnDate).toLocaleString() : 'N/A';
                  
                  // Get balance - use balance_after if available, otherwise show N/A
                  const balanceAfter = txn.balance_after !== undefined && txn.balance_after !== null 
                    ? parseFloat(txn.balance_after).toFixed(2) 
                    : null;
                  
                  return (
                    <div
                      key={txn.transaction_id || index}
                      className={`p-4 bg-muted rounded-xl flex justify-between items-center ${
                        isOrder || orderNo ? 'cursor-pointer hover:bg-muted/70 transition-colors' : ''
                      }`}
                      onClick={() => {
                        if (orderNo) {
                          navigate(`/order/${orderNo}`);
                        }
                      }}
                    >
                      <div>
                        <p className={`font-bold text-primary ${(isOrder || orderNo) ? 'hover:underline' : ''}`}>
                          {txn.description || (orderNo ? `Order #${orderNo}` : 'Transaction')}
                          {(isOrder || orderNo) && <span className="ml-2 text-xs text-accent">(Click to view)</span>}
                        </p>
                        <p className="text-xs text-secondary">
                          {formattedDate}
                        </p>
                      </div>
                      <div className="text-right">
                        <p
                          className={`text-xl font-bold ${
                            txn.type?.toUpperCase() === 'CREDIT' ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {txn.type?.toUpperCase() === 'CREDIT' ? '+' : '-'}₹{Math.abs(parseFloat(txn.amount || 0)).toFixed(2)}
                        </p>
                        <p className="text-xs text-secondary">
                          Balance: ₹{txn.balance_after !== undefined && txn.balance_after !== null 
                            ? parseFloat(txn.balance_after).toFixed(2) 
                            : 'N/A'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-secondary py-8">No transactions yet</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* PIN Verification Modal for Summary */}
      <Dialog open={isVerifySummaryPinOpen} onOpenChange={setIsVerifySummaryPinOpen}>
        <DialogContent className="bg-surface max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-primary flex items-center gap-2">
              <Eye className="h-5 w-5 text-accent" />
              Verify PIN to View Summary
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleVerifySummaryPin} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="summary-pin">Enter your 5-digit PIN</Label>
              <Input
                id="summary-pin"
                type="password"
                value={summaryPin}
                onChange={(e) => setSummaryPin(e.target.value)}
                placeholder="•••••"
                maxLength={5}
                pattern="[0-9]{5}"
                className="h-12 mt-2 text-center text-2xl tracking-widest"
                autoFocus
                required
              />
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                ℹ️ Financial summary will be visible for 10 seconds after verification, then auto-hide for security.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsVerifySummaryPinOpen(false);
                  setSummaryPin('');
                }}
                className="flex-1 h-11"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 h-11 bg-accent text-white hover:bg-accent/90"
              >
                Verify & View
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation */}
      <BottomNavigation
        isOnline={true}
        onAttendanceClick={() => navigate('/')}
        onReportsClick={() => navigate('/?tab=reports')}
      />
      
      {/* Spacer for bottom navigation */}
      <div className="h-16" />

      {/* FOS Watermark */}
      <FOSWatermark opacity={0.06} size="md" />
    </div>
  );
};

export default LedgerManagement;
